'use client';

import React, { useState } from 'react';
import { FileDown, CheckCircle, Loader2, Calendar } from 'lucide-react';

interface WeeklyExportTriggerProps {
  today: string;
  weekLabel: string;
}

type ExportState = 'idle' | 'generating' | 'done';

export default function WeeklyExportTrigger({ today, weekLabel }: WeeklyExportTriggerProps) {
  const [exportState, setExportState] = useState<ExportState>('idle');

  // Determine if today is Thursday (day index 4)
  // Using static data to avoid hydration mismatch
  const isThursday = true; // In production: new Date().getDay() === 4 in a useEffect

  const handleExport = () => {
    if (exportState !== 'idle') return;
    setExportState('generating');

    // Simulate PDF generation — replace with actual PDF export call
    setTimeout(() => {
      setExportState('done');
      setTimeout(() => setExportState('idle'), 3500);
    }, 2200);
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
      {/* Thursday indicator */}
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
        style={{
          backgroundColor: isThursday ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${isThursday ? 'rgba(34,197,94,0.2)' : 'var(--border)'}`,
        }}
      >
        <Calendar size={13} className={isThursday ? 'text-positive' : 'text-muted-foreground'} />
        <span className={isThursday ? 'text-positive font-medium' : 'text-muted-foreground'}>
          {isThursday ? 'Thursday — Export ready' : today}
        </span>
      </div>

      {/* Export button */}
      <button
        onClick={handleExport}
        disabled={exportState === 'generating'}
        className={`
          flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold
          transition-all duration-200 whitespace-nowrap
          ${exportState === 'done'
            ? 'bg-positive/10 text-positive border border-positive/20 cursor-default'
            : exportState === 'generating' ?'bg-primary/10 text-primary border border-primary/20 cursor-not-allowed'
            : isThursday
            ? 'btn-primary' :'btn-secondary opacity-70 hover:opacity-100'
          }
        `}
        title={
          !isThursday
            ? 'Thursday export — available every Thursday at 16:00'
            : 'Generate weekly handover PDF report'
        }
      >
        {exportState === 'generating' ? (
          <>
            <Loader2 size={15} className="animate-spin" />
            Generating PDF…
          </>
        ) : exportState === 'done' ? (
          <>
            <CheckCircle size={15} />
            PDF Downloaded
          </>
        ) : (
          <>
            <FileDown size={15} />
            Export Thursday PDF
          </>
        )}
      </button>
    </div>
  );
}
